// « Miroir Vega » — see what the incumbent PMS sees, without opening it.
//
// `docs/VEGA-MIRROR-BRIEF.md` §9 (VM1), migration 0142.
//
// **Read-only BY CONSTRUCTION.** There is no input on the mirror tabs and no
// write method on the port behind them, so nothing here can push toward Vega even
// by accident (§12: read-only, one direction, always).
//
// The « Relecture » tab (operator-only, `pms.seed.manage`) is the one door that
// acts, and it sits behind ITS OWN port (`VegaSeed`), not this one: it asks the
// PMS to read Vega into its own tables — never the other way round — so the
// guarantee above stays true of `VegaMirror` itself. See `SeedRuns.tsx`.
//
// The columns are deliberately Vega's, not the platform's. §4 of the brief is
// the argument: a mirror is evidence in the shape the evidence arrived in, and
// one you cannot check against Vega's own screen is not a mirror. That is the
// opposite of « Occupation », whose whole design is that the board never learns
// which system it is reading — and the difference is intentional.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { todayIsoParis } from '@lemontaigu/staff';
// The type de séjour's one label set (group edit Q10) — never spelled out here.
import { PMS_BOARD_LABELS } from '@lemontaigu/pms-core';
import type {
  MirrorCancellation,
  MirrorFolioLine,
  MirrorInvoice,
  MirrorPayment,
  MirrorReservation,
} from '@lemontaigu/pms-core';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import { usePms, usePmsScope } from './PmsProvider.js';
import { fmtDayShort, isoAddDays } from './logic.js';
import { SeedRuns } from './SeedRuns.js';

type Tab = 'reservations' | 'cancellations' | 'payments' | 'invoices' | 'folio' | 'seed';

/** The FACT AUTO plan is deliberately not windowed — see the port. */
const RANGED: ReadonlySet<Tab> = new Set<Tab>([
  'reservations', 'cancellations', 'payments', 'invoices',
]);

const RANGES = [
  { key: 'week', label: '7 jours', back: 0, forward: 7 },
  { key: 'month', label: '30 jours', back: 0, forward: 30 },
  { key: 'past', label: '30 j. passés', back: 30, forward: 0 },
] as const;

type RangeKey = (typeof RANGES)[number]['key'];

/** Vega's status letters, in French, with the caveat attached where it is
 *  read rather than in a comment nobody opens. */
const STATUS_FR: Record<NonNullable<MirrorReservation['status']>, string> = {
  reserved: 'Réservé',
  confirmed: 'Confirmé',
  in_house: 'Arrivé',
  departed: 'Parti',
};


const money = (n: number | null) =>
  n == null ? '—' : n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

/** A movement's amount is SIGNED, and null is not zero: a règlement paired with
 *  a deposit deduction carries nothing because the deposit settled it. Rendering
 *  that as « 0,00 € » would state something false, so it gets its own glyph and
 *  its own explanation in the header. */
const movementAmount = (n: number | null) =>
  n == null ? <span className="muted" title="Rien n'a bougé sur cette ligne : les arrhes ont soldé la facture, et le montant est porté par la ligne « arrhes déduites ».">—</span>
            : <span className={n < 0 ? 'occ-neg' : undefined}>{money(n)}</span>;

const qty = (n: number | null) =>
  n == null ? '—' : n.toLocaleString('fr-FR', { maximumFractionDigits: 3 });

export function Mirror() {
  const { vegaMirror } = usePms();
  const { current } = usePmsScope();
  const canSeed = useCapability(CAPABILITIES.PMS_SEED_MANAGE);
  const [tab, setTab] = useState<Tab>('reservations');
  const [rangeKey, setRangeKey] = useState<RangeKey>('week');
  const [resas, setResas] = useState<MirrorReservation[]>([]);
  const [cancels, setCancels] = useState<MirrorCancellation[]>([]);
  const [payments, setPayments] = useState<MirrorPayment[]>([]);
  const [invoices, setInvoices] = useState<MirrorInvoice[]>([]);
  const [folio, setFolio] = useState<MirrorFolioLine[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const range = useMemo(() => {
    const r = RANGES.find((x) => x.key === rangeKey) ?? RANGES[0];
    const today = todayIsoParis();
    return { from: isoAddDays(today, -r.back), to: isoAddDays(today, r.forward) };
  }, [rangeKey]);

  const load = useCallback(async () => {
    // The « Relecture » tab reads through its own port and loads itself.
    if (!current || tab === 'seed') return;
    setLoading(true);
    setError(null);
    const res =
      tab === 'reservations'
        ? await vegaMirror.reservations(current, range.from, range.to)
        : tab === 'cancellations'
          ? await vegaMirror.cancellations(current, range.from, range.to)
          : tab === 'payments'
            ? await vegaMirror.payments(current, range.from, range.to)
            : tab === 'invoices'
              ? await vegaMirror.invoices(current, range.from, range.to)
              : await vegaMirror.folioLines(current);
    setLoading(false);
    if (!res.ok) {
      setError(res.error.message ?? 'lecture impossible');
      return;
    }
    if (tab === 'reservations') setResas(res.value as MirrorReservation[]);
    else if (tab === 'cancellations') setCancels(res.value as MirrorCancellation[]);
    else if (tab === 'payments') setPayments(res.value as MirrorPayment[]);
    else if (tab === 'invoices') setInvoices(res.value as MirrorInvoice[]);
    else setFolio(res.value as MirrorFolioLine[]);
  }, [vegaMirror, current, tab, range.from, range.to]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="occ">
      <header className="occ-head">
        <div>
          <h1>Miroir Vega</h1>
          <p className="muted">
            Ce que Vega détient, tel qu’il le détient. Lecture seule : ces onglets n’ont aucune
            saisie, donc rien ne peut repartir vers Vega, même par erreur. L’onglet Relecture
            (opérateur) demande au PMS de relire Vega — jamais l’inverse.
          </p>
          {(tab === 'reservations' || tab === 'cancellations') && (
            <>
              <p className="muted occ-note">
                ⚠ Le <strong>statut</strong> est celui de Vega, et c’est un indice, pas une
                position : le 05/09/2026, 149 réservations étaient parties tout en affichant
                encore « Réservé » ou « Confirmé ». Fiez-vous aux dates.
              </p>
              <p className="muted occ-note">
                ⚠ Une réservation <strong>annulée quitte le livre</strong> dans Vega : les deux
                onglets sont disjoints, l’annulation n’est pas un statut de la réservation. Les
                annulations incluent des couverts <strong>restaurant</strong>, d’où la colonne
                « Point de vente ».
              </p>
            </>
          )}
          {tab === 'payments' && (
            <>
              <p className="muted occ-note">
                ⚠ Le <strong>payeur</strong> est le nom porté par le mouvement lui-même, et
                c’est le seul nom fiable ici. La colonne « Client » est <strong>vide sur
                tout mouvement qui a encaissé</strong> : Vega y réinscrit le code du mode de
                règlement au lieu d’un identifiant client. Ce vide est donc correct, pas une
                donnée manquante — avant le 14/09/2026, 4 327 règlements étaient rattachés à
                un client qui n’avait rien payé.
              </p>
              <p className="muted occ-note">
                ⚠ Deux colonnes de montant, et elles ne disent pas la même chose.
                <strong> Réglé</strong> est ce que ce mouvement a soldé — c’est l’argent.
                <strong> Total facture</strong> est le total de la facture, <strong>répété à
                l’identique</strong> sur chacune de ses lignes : le totaliser compterait deux
                fois toute facture réglée en partie par des arrhes. Jusqu’au 15/09/2026 nous
                ne lisions que la seconde.
              </p>
              <p className="muted occ-note">
                ⚠ Un montant <strong>vide n’est pas zéro</strong> : rien n’a bougé sur cette
                ligne. Les montants <strong>négatifs</strong> sont réels (avoirs, extournes).
              </p>
            </>
          )}
          {tab === 'folio' && (
            <>
              <p className="muted occ-note">
                Le <strong>plan de facturation automatique</strong> (« FACT AUTO » de Vega) :
                chaque prestation saisie sur une réservation — chambre, taxe de séjour,
                demi-pension ou tout autre produit — avec le prix unitaire facturé chaque
                jour à partir de sa date. Ce n’est ni la note en cours ni une facture : la
                note en cours est tenue à part par Vega.
              </p>
              <p className="muted occ-note">
                ⚠ Affiché <strong>en entier, sans filtre de date</strong>, et c’est voulu :
                ce plan remonte jusqu’en 2016. Une fenêtre par défaut masquerait
                précisément ce qu’on vient y chercher.
              </p>
            </>
          )}
          {tab === 'invoices' && (
            <>
              <p className="muted occ-note">
                Les <strong>factures soldées</strong>, telles que Vega les a émises. Le plan de
                facturation automatique n’est pas une facture : il décrit ce que Vega
                facturera, pas ce qui a été facturé, et les deux onglets ne sont pas deux
                vues de la même chose.
              </p>
              <p className="muted occ-note">
                ⚠ Le <strong>total n’existe pas</strong> dans Vega : il est la somme des
                arrhes appliquées et du règlement. Vérifié sur la facture 26002307 —
                991,92 € d’arrhes + 26,00 € en carte = 1 017,92 €, le total imprimé.
              </p>
              <p className="muted occ-note">
                ⚠ Un total à <strong>0,00 € est réel</strong> : 735 factures sont des
                tickets de passage au bar sans vente, que Vega affiche bien à zéro.
              </p>
              <p className="muted occ-note">
                ⚠ Les quatre colonnes de <strong>TVA</strong> se totalisent à l’euro près
                jusqu’au total imprimé — vérifié sur la facture 26002291 : 320,60 € à 10 %
                + 13,50 € à 20 % (un Martini, deux ballons de Madiran) + 6,64 € exonéré (la
                taxe de séjour) = 340,74 €. Jusqu’au 15/09/2026 ce même champ à 10 % était
                affiché comme « total moins la taxe de séjour » — vrai uniquement sur une
                facture sans ligne à 20 %.
              </p>
            </>
          )}
        </div>
        <div className="occ-ranges" hidden={!RANGED.has(tab)}>
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              className={r.key === rangeKey ? 'is-active' : ''}
              onClick={() => setRangeKey(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      <div className="occ-ranges" style={{ marginBottom: '0.75rem' }}>
        <button
          type="button"
          className={tab === 'reservations' ? 'is-active' : ''}
          onClick={() => setTab('reservations')}
        >
          Réservations
        </button>
        <button
          type="button"
          className={tab === 'cancellations' ? 'is-active' : ''}
          onClick={() => setTab('cancellations')}
        >
          Annulations
        </button>
        <button
          type="button"
          className={tab === 'payments' ? 'is-active' : ''}
          onClick={() => setTab('payments')}
        >
          Règlements
        </button>
        <button
          type="button"
          className={tab === 'invoices' ? 'is-active' : ''}
          onClick={() => setTab('invoices')}
        >
          Factures
        </button>
        <button
          type="button"
          className={tab === 'folio' ? 'is-active' : ''}
          onClick={() => setTab('folio')}
        >
          Plan FACT AUTO
        </button>
        {canSeed && (
          <button
            type="button"
            className={tab === 'seed' ? 'is-active' : ''}
            onClick={() => setTab('seed')}
          >
            Relecture
          </button>
        )}
      </div>

      {tab === 'seed' && <SeedRuns />}

      {error && <p className="occ-error">⚠ {error}</p>}
      {loading && <p className="muted">Chargement…</p>}

      {!loading && tab === 'reservations' && (
        <table className="occ-table">
          <thead>
            <tr>
              <th>N° résa</th>
              <th>Client</th>
              <th>Séjour</th>
              <th>Statut</th>
              <th>Type</th>
              <th>Séj.</th>
              <th title="Le donneur d'ordre tel que Vega l'enregistre — centrale, moteur de réservation, tour-opérateur ou téléphone. Vide = pris à la réception.">
                Source
              </th>
              <th title="La référence de la source. Seul endroit où l'OTA d'origine peut apparaître : toutes les OTA passant par le channel manager partagent une seule source.">
                Réf.
              </th>
              <th>Montant</th>
            </tr>
          </thead>
          <tbody>
            {resas.map((r) => (
              <tr key={r.resa}>
                <th scope="row">{r.resa}</th>
                <td>{r.bookerName ?? '—'}</td>
                <td className="muted">
                  {r.arrivalDate ? fmtDayShort(r.arrivalDate) : '?'} →{' '}
                  {r.departureDate ? fmtDayShort(r.departureDate) : '?'}
                </td>
                <td className="muted">{r.status ? STATUS_FR[r.status] : '—'}</td>
                <td className="muted">{r.partyKind === 'group' ? 'Groupe' : 'Individuel'}</td>
                <td className="muted">{r.board ? PMS_BOARD_LABELS[r.board] : '—'}</td>
                <td>{r.sourceName ?? <span className="muted">réception</span>}</td>
                <td className="muted">{r.externalRef ?? '—'}</td>
                <td className="muted occ-amount">{money(r.channelAmount)}</td>
              </tr>
            ))}
            {resas.length === 0 && (
              <tr>
                <td colSpan={9} className="muted">
                  Aucune réservation sur cette période.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {!loading && tab === 'cancellations' && (
        <table className="occ-table">
          <thead>
            <tr>
              <th>Annulée le</th>
              <th>N° résa</th>
              <th>Séjour prévu</th>
              <th>Chambre</th>
              <th>Nb</th>
              <th>Pers.</th>
              <th title="« CA perdu » — le total de la RÉSERVATION, pas un prix par nuit. Vide sur un compte maison restaurant, qui n'a pas de prix chambre.">
                CA perdu
              </th>
              <th>Point de vente</th>
              <th>Motif</th>
            </tr>
          </thead>
          <tbody>
            {cancels.map((c, i) => (
              <tr key={`${c.resa ?? 'x'}-${c.cancelledOn ?? i}-${i}`}>
                <th scope="row">{c.cancelledOn ? fmtDayShort(c.cancelledOn) : '—'}</th>
                <td className="muted">{c.resa ?? '—'}</td>
                <td className="muted">
                  {c.arrivalDate ? fmtDayShort(c.arrivalDate) : '?'} →{' '}
                  {c.departureDate ? fmtDayShort(c.departureDate) : '?'}
                </td>
                <td className="muted">{c.room ?? '—'}</td>
                <td className="muted">{c.rooms ?? '—'}</td>
                <td className="muted">{c.adults ?? '—'}</td>
                <td className="muted">{money(c.caPerdu)}</td>
                <td className="muted">
                  {c.outlet === 'restaurant' ? 'Restaurant' : c.outlet === 'hotel' ? 'Hôtel' : '—'}
                </td>
                <td className="muted">{c.reason ?? '—'}</td>
              </tr>
            ))}
            {cancels.length === 0 && (
              <tr>
                <td colSpan={9} className="muted">
                  Aucune annulation sur cette période.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {!loading && tab === 'payments' && (
        <table className="occ-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Mouvement</th>
              <th title="Règlement, arrhes déduites et prise en charge pointent vers une FACTURE ; arrhes versées et prise en charge (E) portent un numéro d'ARRHES. La référence ne se lit qu'avec le type.">
                Réf.
              </th>
              <th title="Le nom porté par le mouvement : le seul nom fiable sur cette ligne.">
                Payeur
              </th>
              <th>Mode</th>
              <th title="Vide sur tout mouvement ayant encaissé : Vega y réinscrit le code du mode de règlement, pas un identifiant client. Ce vide est correct.">
                Client
              </th>
              <th>Libellé</th>
              <th title="Ce que CE mouvement a réellement soldé — « Mt réglé » sur le journal de Vega. C'est l'argent.">
                Réglé
              </th>
              <th title="Le total de la FACTURE que ce mouvement solde, répété à l'identique sur chacune de ses lignes de règlement — « Mt total ». Ne se totalise pas.">
                Total facture
              </th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p, i) => (
              <tr key={`${p.reference ?? 'x'}-${p.movementDate ?? ''}-${i}`}>
                <th scope="row">{p.movementDate ? fmtDayShort(p.movementDate) : '—'}</th>
                <td className="muted">{p.kindLabel ?? p.kind ?? '—'}</td>
                <td className="muted">{p.reference ?? '—'}</td>
                <td>{p.name ?? '—'}</td>
                <td className="muted">{p.method ?? '—'}</td>
                <td className="muted">{p.clientRef ?? '—'}</td>
                <td className="muted">{p.label ?? '—'}</td>
                <td className="occ-amount">{movementAmount(p.settledAmount)}</td>
                <td className="muted occ-amount">{money(p.invoiceTotal)}</td>
              </tr>
            ))}
            {payments.length === 0 && (
              <tr>
                <td colSpan={9} className="muted">
                  Aucun mouvement sur cette période.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {!loading && tab === 'invoices' && (
        <table className="occ-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>N° facture</th>
              <th>Client</th>
              <th title="L'identifiant client au fichier. Fiable ici : le nom de la facture concorde avec la fiche client dans 99,23 % des cas.">
                Fiche
              </th>
              <th title="Les arrhes appliquées à cette facture.">Arrhes</th>
              <th title="Ce qui a été encaissé au moment du solde — carte, espèces.">
                Règlement
              </th>
              <th title="La part de la facture taxée à 10 % — chambres, petits déjeuners, menus, la plupart des plats et boissons sans alcool.">
                10 %
              </th>
              <th title="La part taxée à 20 % — alcool (cave à vins, boissons alcoolisées), et deux groupes de comptabilité analytique annexes (location, cartes postales).">
                20 %
              </th>
              <th title="La part exonérée de TVA — la taxe de séjour, les pourboires, les débours.">
                Exonéré
              </th>
              <th title="La part taxée à 5,5 %. Présente dans le format Vega ; à 0,00 € sur toutes les factures observées jusqu'ici.">
                5,5 %
              </th>
              <th title="Arrhes + règlement. Vega ne stocke aucun champ « total » : c'est la somme des deux — qui est aussi la somme des quatre colonnes de TVA.">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((f) => (
              <tr key={f.facture}>
                <th scope="row">{f.invoiceDate ? fmtDayShort(f.invoiceDate) : '—'}</th>
                <td className="muted">{f.facture}</td>
                <td>{f.name ?? '—'}</td>
                <td className="muted">{f.clientRef ?? '—'}</td>
                <td className="muted occ-amount">{money(f.depositApplied)}</td>
                <td className="muted occ-amount">{money(f.settledAmount)}</td>
                <td className="muted occ-amount">{money(f.subtotal10Pct)}</td>
                <td className="muted occ-amount">{money(f.subtotal20Pct)}</td>
                <td className="muted occ-amount">{money(f.subtotalExempt)}</td>
                <td className="muted occ-amount">{money(f.subtotal5_5Pct)}</td>
                <td className="occ-amount">{money(f.invoiceTotal)}</td>
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr>
                <td colSpan={11} className="muted">
                  Aucune facture sur cette période.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {!loading && tab === 'folio' && (
        <table className="occ-table">
          <thead>
            <tr>
              <th title="Début : le premier jour facturé.">Début</th>
              <th title="Fin : exclue. La ligne est facturée chaque jour de Début jusqu'à la veille de Fin.">
                Fin
              </th>
              <th title="Jusqu'où la facturation automatique de Vega a déjà porté cette ligne sur la note en cours. Vide : pas encore commencée.">
                Posté jusqu'au
              </th>
              <th title="Le numéro de RÉSERVATION. À ne jamais confondre avec le numéro client de la colonne suivante : les deux espaces d'identifiants se recouvrent.">
                N° résa
              </th>
              <th title="L'identifiant CLIENT au fichier. Deux colonnes, deux espaces distincts.">
                Client
              </th>
              <th>Libellé</th>
              <th title="Le code produit au catalogue Vega — ce qui permet de vérifier une prestation contre le catalogue plutôt que contre son propre texte.">
                Produit
              </th>
              <th>Qté</th>
              <th>P.U.</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {folio.map((f, i) => (
              <tr key={`${f.resa ?? 'x'}-${f.lineDate ?? ''}-${i}`}>
                <th scope="row">{f.lineDate ? fmtDayShort(f.lineDate) : '—'}</th>
                <td className="muted">{f.endDate ? fmtDayShort(f.endDate) : '—'}</td>
                <td className="muted">{f.postedThrough ? fmtDayShort(f.postedThrough) : '—'}</td>
                <td className="muted">{f.resa ?? '—'}</td>
                <td className="muted">{f.guestRef ?? '—'}</td>
                <td>{f.label ?? '—'}</td>
                <td className="muted">{f.productCode ?? '—'}</td>
                <td className="muted">{qty(f.qty)}</td>
                <td className="muted">{money(f.unitPrice)}</td>
                <td className="occ-amount">{money(f.total)}</td>
              </tr>
            ))}
            {folio.length === 0 && (
              <tr>
                <td colSpan={10} className="muted">
                  Aucune ligne de plan de facturation.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
